import { isAlertEligibleInformation, type InformationState } from "./informationState";
import type { EventTracking, SourceRevisionIdentity } from "./sourceRevision";
export const ALERT_CATEGORIES = [
  "weather",
  "earthquake",
  "health",
  "geopolitics",
  "market-news",
  "news",
  "markets",
  "fixed-income",
  "forex",
  "energy",
  "crypto",
  "macro",
  "supply-chain",
  "regulation",
] as const;

export const ALERT_SEVERITIES = ["low", "medium", "high", "critical"] as const;
export const ALERT_CONFIDENCES = ["low", "medium", "high"] as const;

export type AlertCategory = typeof ALERT_CATEGORIES[number];
export type AlertSeverity = typeof ALERT_SEVERITIES[number];
export type AlertConfidence = typeof ALERT_CONFIDENCES[number];

export interface AlertWatchlist {
  id: string;
  name: string;
  terms: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AlertRule {
  id: string;
  watchlistId: string;
  name: string;
  categories: AlertCategory[];
  minSeverity: AlertSeverity;
  minConfidence: AlertConfidence;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AlertSignalCandidate {
  id?: string;
  category: string;
  source?: string;
  severity: AlertSeverity;
  confidence: AlertConfidence;
  status: string;
  information?: InformationState;
  expiresAt?: string;
  eventIdentity?: SourceRevisionIdentity;
  eventTracking?: EventTracking;
  title: string;
  summary: string;
  newsEvidence?: Array<{ title: string; description?: string | null }>;
  marketImpact?: string;
  drivers?: string[];
}

const severityRank: Record<AlertSeverity, number> = {
  low: 0,
  medium: 1,
  high: 2,
  critical: 3,
};

const confidenceRank: Record<AlertConfidence, number> = {
  low: 0,
  medium: 1,
  high: 2,
};

const AGGREGATE_NEWS_SIGNAL_IDS = new Set([
  "finance:central-bank-watch",
  "finance:supply-chain",
  "finance:financial-regulation",
  "finance:economic-news",
]);

export function normalizeAlertTerm(value: string): string {
  return value
    .replace(/[\u0000-\u001F\u007F]/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("en-US");
}

export function normalizeAlertTerms(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const value of values) {
    const normalized = normalizeAlertTerm(value);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    terms.push(normalized);
  }
  return terms;
}

export function isAlertEligibleSignal(signal: Pick<AlertSignalCandidate, "status" | "information" | "expiresAt" | "eventIdentity" | "eventTracking">, nowMs = Date.now()): boolean {
  if (signal.eventTracking?.status === "unavailable") return false;
  if (signal.eventIdentity && (signal.eventIdentity.relation !== "current" || signal.eventIdentity.kind === "retraction"
    || signal.eventIdentity.material.reasons.includes("source-withdrawn-or-nonactual"))) return false;
  // A supplied source deadline is authoritative even for legacy live records.
  // Invalid and boundary-equal deadlines cannot fall back to a queue lifetime.
  if (signal.expiresAt !== undefined) {
    const expiresAt = Date.parse(signal.expiresAt);
    if (!Number.isFinite(expiresAt) || expiresAt <= nowMs) return false;
  }
  return signal.information ? isAlertEligibleInformation(signal.information, nowMs) : signal.status === "live";
}

function matchesAlertTerm(text: string, term: string): boolean {
  const escaped = normalizeAlertTerm(term).replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+");
  if (!escaped) return false;
  // Match a complete token or phrase. This keeps terms such as "sec" from
  // matching "second" while still treating punctuation in user terms literally.
  return new RegExp(`(^|[^\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`, "iu").test(text);
}

export function matchesAlertWatchlist(
  watchlist: Pick<AlertWatchlist, "terms">,
  signal: AlertSignalCandidate,
  nowMs = Date.now(),
): boolean {
  if (!isAlertEligibleSignal(signal, nowMs)) return false;
  const aggregateNewsCard = AGGREGATE_NEWS_SIGNAL_IDS.has(signal.id ?? "");
  const sourceArticle = signal.source?.toLocaleLowerCase("en-US") === "gdelt" && Boolean(signal.newsEvidence?.length);
  const newsOnly = signal.category === "market-news" || signal.category === "news" || aggregateNewsCard || sourceArticle;
  const text = newsOnly
    ? signal.newsEvidence?.flatMap((article) => [article.title, article.description]) ?? []
    : [signal.title, signal.summary, ...(signal.newsEvidence ?? []).flatMap((article) => [article.title, article.description]), ...(signal.drivers ?? [])];
  const haystack = text
    .filter((value): value is string => typeof value === "string" && value.length > 0)
    .join(" ")
    .toLocaleLowerCase("en-US");
  return watchlist.terms.some((term) => matchesAlertTerm(haystack, term));
}

export function matchesAlertRule(
  rule: Pick<AlertRule, "categories" | "enabled" | "minConfidence" | "minSeverity">,
  watchlist: Pick<AlertWatchlist, "terms">,
  signal: AlertSignalCandidate,
  nowMs = Date.now(),
): boolean {
  if (!rule.enabled || !matchesAlertWatchlist(watchlist, signal, nowMs)) return false;
  if (rule.categories.length > 0 && !rule.categories.includes(signal.category as AlertCategory)) return false;
  if (severityRank[signal.severity] < severityRank[rule.minSeverity]) return false;
  return confidenceRank[signal.confidence] >= confidenceRank[rule.minConfidence];
}
